import { useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useGetRecordRepresentation,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { Company, Contact, Task } from "../types";
import { completeCampaignTask } from "./completeCampaignTask";

export const CompleteCampaignTaskDialog = ({
  task,
  open,
  onOpenChange,
  onCompleted,
}: {
  task: Task;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: campaign } = useGetOne(
    "campaigns",
    { id: task.campaign_id! },
    { enabled: !!task.campaign_id && open },
  );
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: task.contact_id! },
    { enabled: !!task.contact_id && open },
  );
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: task.company_id! },
    { enabled: !task.contact_id && !!task.company_id && open },
  );

  const getContactRepresentation = useGetRecordRepresentation("contacts");
  const getCompanyRepresentation = useGetRecordRepresentation("companies");

  const targetHint = task.contact_id
    ? translate("resources.campaigns.complete_note.saved_to_contact", {
        name: contact ? getContactRepresentation(contact) : `#${task.contact_id}`,
        _: "Note will be saved to contact: %{name}",
      })
    : task.company_id
      ? translate("resources.campaigns.complete_note.saved_to_company", {
          name: company ? getCompanyRepresentation(company) : `#${task.company_id}`,
          _: "Note will be saved to company: %{name}",
        })
      : null;

  const handleComplete = async (withNote: boolean) => {
    if (!identity) return;
    setSaving(true);
    try {
      await completeCampaignTask(dataProvider, task, {
        noteText: withNote ? noteText : null,
        identityId: identity.id,
        campaignName: campaign?.name ?? "Campaign",
      });
      notify("resources.campaigns.complete_note.success", {
        type: "info",
        messageArgs: { _: "Task completed" },
      });
      setNoteText("");
      onOpenChange(false);
      onCompleted?.();
      refresh();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate("resources.campaigns.complete_note.title", {
              _: "Add note to task",
            })}
          </DialogTitle>
          {targetHint && <DialogDescription>{targetHint}</DialogDescription>}
        </DialogHeader>

        <textarea
          className="flex min-h-[100px] w-full rounded-md border border-input px-3 py-2 text-sm"
          placeholder={translate(
            "resources.campaigns.complete_note.placeholder",
            { _: "Write a note about this visit or call…" },
          )}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          disabled={saving}
        />

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            type="button"
            onClick={() => handleComplete(true)}
            disabled={saving || !noteText.trim()}
          >
            {translate("resources.campaigns.complete_note.save", {
              _: "Save note",
            })}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleComplete(false)}
            disabled={saving}
          >
            {translate("resources.campaigns.complete_note.skip", {
              _: "Complete without note",
            })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {translate("ra.action.cancel")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
